import { useAppSelector } from '@/lib/redux/store';
import Link from 'next/link';
import { useRouter } from 'next/router';
import React, { useEffect, useRef } from 'react';
import Image from 'next/image';
import Head from "next/head";

const Page = () =>
{
    const router = useRouter();
    const store = useAppSelector((state) => state.app);
    const { user } = store;
    const whyInstant = useRef(null);
    useEffect(() =>
    {
        if (user)
        {
            router.push('/dashboard');
        }
    }, [
        user,
        router
    ]);

    /* SEO VARIABLES */
    const title =
        "Real-Time Polling Software for Meetings, Events & Teams | Instant Polls";

    const description =
        "Create live polls, collect instant audience feedback, and improve engagement with real-time polling software for meetings, classrooms, and events.";

    const canonical = "https://instantpolls.online/";

    const softwareJsonLd = {
        "@context": "https://schema.org",
        "@type": "SoftwareApplication",
        name: "Instant Polls",
        applicationCategory: "BusinessApplication",
        operatingSystem: "Web",
        url: canonical,
        description:
            "Real-time polling software to create live polls and collect instant audience feedback for meetings, events, and classrooms.",
    };

    /* FAQ SCHEMA */

    const faqJsonLd = {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: [
            {
                "@type": "Question",
                name: "What is real-time polling software?",
                acceptedAnswer: {
                    "@type": "Answer",
                    text: "Real-time polling software lets you create live polls and see responses instantly during meetings, events, or classes."
                }
            },
            {
                "@type": "Question",
                name: "Can I use Instant Polls for meetings and events?",
                acceptedAnswer: {
                    "@type": "Answer",
                    text: "Yes, Instant Polls helps teams, event hosts, and educators collect instant audience feedback in real time."
                }
            }
        ]
    };

    const scrollToBottom = () =>
    {
        whyInstant.current?.scrollIntoView({ behavior: 'smooth' });
    }

    return (

        <>
            <Head>
                <title>{title}</title>
                <meta name="description" content={description} />
                <link rel="canonical" href={canonical} />
                <meta name="robots" content="index,follow" />
                {/* Open Graph */}
                <meta property="og:title" content={title} />
                <meta property="og:description" content={description} />
                <meta property="og:url" content={canonical} />
                <meta property="og:type" content="website" />
                <meta property="og:image" content="https://instantpolls.online/og-image.jpg" />

                {/* Twitter */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={title} />
                <meta name="twitter:description" content={description} />
                <meta name="twitter:image" content="https://instantpolls.online/og-image.jpg" />

                {/* Software Schema */}

                <script
                    type="application/ld+json"
                    dangerouslySetInnerHTML={{ __html: JSON.stringify(softwareJsonLd) }}
                />

                {/* FAQ Schema */}

                <script
                    type="application/ld+json"
                    dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }}
                />

            </Head>

            <div>


                {/* new ui design */}
                <section className="hero-reseller d-flex align-items-center text-white">
                    <div className="container hero-inner">
                        <div className="hero-copy">
                            <h1>Better Questions.<br />Better Solutions.</h1>
                            <p className='mb-1'>
                                Bring your audience together by making them part of the story.
                                <b> Instant Polls </b> is interactive voting software that turns listeners into active participants in real-time
                            </p>
                            <p className='mb-1'>Lead with confidence by knowing exactly what your audience is thinking.</p>
                            <p className='mb-5'>
                                <b> Instant Polls </b> helps you create professional live polls to turn passive listeners into active contributors.
                            </p>
                            <div className="d-flex  flex-lg-row flex-wrap gap-3">
                                <Link className="button btn-purple" href="/contact">BOOK A DEMO</Link>
                                <Link className="button btn-white" href="/register">START FREE TRIAL</Link>
                                <Link className="button btn-purple" href="/pricing">VIEW PRICING</Link>
                                <Link className="button btn-white" href="/register">REGISTER</Link>
                            </div>
                        </div>
                    </div>
                </section>
                <section className="py-5 text-center threecards">
                    <div className="container public-fixed-width">
                        <div className="row g-5 align-items-stretch">
                            <div className="col-md-4 col-sm-12 d-flex">
                                <div className="cardthree problem p-4 w-100">
                                    <h5 className="fw-head d-flex align-items-center justify-content-start text-start">
                                        <Image
                                            src="/public/polling-problem-statement-icon.png"
                                            alt="Problem with traditional polling tools"
                                            width={40}
                                            height={40}
                                            className="footerLogo height40 me-2"
                                            priority
                                        />
                                        <span>The Problem</span>
                                    </h5>
                                    <p>Polling tools are too slow and generic</p>
                                </div>
                            </div>

                            <div className="col-md-4 col-sm-12 d-flex">
                                <div className="cardthree insight p-4 w-100">
                                    <h5 className="fw-head d-flex align-items-center justify-content-start text-start">
                                        <Image
                                            src="/public/polling-solution-lightbulb-icon.png"
                                            alt="Insight Icon"
                                            width={40}
                                            height={40}
                                            className="footerLogo height40 me-2"
                                        />
                                        <span>The Insight</span>
                                    </h5>
                                    <p>Insight is only powerful when it’s timely</p>
                                </div>
                            </div>
                            <div className="col-md-4 col-sm-12 d-flex">
                                <div className="cardthree solution p-4 w-100 cursorPointer" onClick={scrollToBottom}>
                                    <h5 className="fw-head d-flex align-items-center justify-content-start text-start">
                                        <Image
                                            src="/public/polling-growth-icon.png"
                                            alt="Solution Icon"
                                            width={40}
                                            height={40}
                                            className="footerLogo height40 me-2"
                                        />
                                        <span>Why Instant Polls</span>
                                    </h5>
                                    <p>Real-time branded polling that gives clarity in the moment</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
                <section className="position-relative">
                    <div className="container public-fixed-width">
                        <div className="row bg-white rounded">
                            <div className="col-12">
                                <div className="video-wrapper">
                                    <video
                                        className="custom-video"
                                        controls
                                        preload="metadata"

                                        poster="/industry/instant-polls-intro-video-poster.png"
                                    >
                                        <source
                                            src="https://instant-polls.syd1.cdn.digitaloceanspaces.com/videos/instantpolls-intro.mp4"
                                            type="video/mp4"
                                        />
                                    </video>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
                <section className="py-5 text-center">
                    <div className="container ">
                        <div className="industries mx-4">
                            <h2 className="mb-4 text-purple">How Can Instant Polls Be Applied</h2>
                            <div className="row g-4">
                                <div className="col-md-3 col-12">

                                    <div className="card pill1">
                                        <div className="cardimage1">
                                            <h6>Align your team in every meeting </h6> </div>
                                        <p className='textUses'><Link className='text-white' href="/business_leadership"> Business & Leadership</Link></p>
                                    </div>
                                </div>
                                <div className="col-md-3 col-12">
                                    <div className="card pill2">
                                        <div className="cardimage2">
                                            <h6>Gauge public sentiment in real time</h6></div>
                                        <p className='textUses'><Link className='text-white' href="/government">Government Associations</Link></p>
                                    </div>

                                </div>
                                <div className="col-md-3 col-12">
                                    <div className="card pill3">
                                        <div className="cardimage3">
                                            <h6>+250% response rate</h6></div>

                                        <p className='textUses'><Link className="text-white" href="/media">Media Features</Link></p>
                                    </div>

                                </div>

                                <div className="col-md-3 col-12">
                                    <div className="card pill4">
                                        <div className="cardimage4">
                                            <h6>Test creative, concepts, and campaigns</h6></div>
                                        <p className='textUses'><Link className="text-white" href="/consulting">Consulting and Professional Services</Link></p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                <section className="py-3 position-relative">
                    <div className="container instant-poll" ref={whyInstant}>
                        <h2 className="text-instant">Why Instant Polls</h2>
                        <div className="row justify-content-between align-items-start zIndex9999">
                            <div className="col-lg-7">
                                <ul className="list-unstyled mt-4">

                                    <li className="d-flex align-items-start mb-3">
                                        <Image
                                            src="/public/real-time-feedback.png"
                                            alt="Real-Time Feedback Icon"
                                            width={40}
                                            height={40}
                                            className="me-2"
                                            quality={75}
                                        />
                                        <span>
                                            <strong>Real-Time Feedback</strong> Launch <b> live polls </b> and see <b> interactive results </b> instantly to keep your audience engaged.
                                        </span>
                                    </li>

                                    <li className="d-flex align-items-start mb-3">
                                        <Image
                                            src="/public/ai-powered-sentiment.png"
                                            alt="AI-powered Sentiment"
                                            width={40}
                                            height={40}
                                            className="me-2"
                                            quality={75}
                                        />
                                        <span>
                                            <strong>AI-Powered Sentiment</strong> Use <b>sentiment analysis </b> to capture the emotional pulse and <b> audience insights </b> from your data.
                                        </span>
                                    </li>

                                    <li className="d-flex align-items-start mb-3">
                                        <Image
                                            src="/public/white-label-branding.png"
                                            alt="White Label Branding"
                                            width={40}
                                            height={40}
                                            className="me-2"
                                            quality={75}
                                        />
                                        <span>
                                            <strong>White-Label Branding</strong> Create <b> custom branded polls </b> that maintain your professional identity from start to finish.
                                        </span>
                                    </li>

                                    <li className="d-flex align-items-start mb-3">
                                        <Image
                                            src="/public/secure-anonymous.png"
                                            alt="Secure and Anonymous"
                                            width={40}
                                            height={40}
                                            className="me-2"
                                            quality={75}
                                        />
                                        <span>
                                            <strong>Secure & Anonymous</strong> Foster honest <b> employee feedback </b> with <b>  enterprise-grade</b> encryption and total anonymity.
                                        </span>
                                    </li>

                                    <li className="d-flex align-items-start mb-3">
                                        <Image
                                            src="/public/smart-segmentation.png"
                                            alt="Smart Segmentation"
                                            width={40}
                                            height={40}
                                            className="me-2"
                                            quality={75}
                                        />
                                        <span>
                                            <strong>Smart Segmentation</strong> Access deep <b> data analytics </b> by filtering results by department, location, or demographics.
                                        </span>
                                    </li>

                                    <li className="d-flex align-items-start mb-3">
                                        <Image
                                            src="/public/monetizable-engagement.png"
                                            alt="Monetisable Engagement"
                                            width={40}
                                            height={40}
                                            className="me-2"
                                            quality={75}
                                        />
                                        <span>
                                            <strong>Monetisable Engagement</strong> Drive  <b>sponsor ROI </b> and revenue with interactive, branded questions built into your event.
                                        </span>
                                    </li>

                                </ul>

                                <Link
                                    className="btn btn-info text-white mt-3"
                                    href="/contact"
                                >
                                    BOOK A DEMO
                                </Link>
                                <Link
                                    className="btn btn-info text-white mt-3"
                                    href="/industry_questions"
                                >
                                    VIEW EXPERT QUESTIONS
                                </Link>
                                <Link
                                    className="btn btn-info text-white mt-3"
                                    href="/pricing"
                                >
                                    VIEW PRICING
                                </Link>
                                <Link
                                    className="btn btn-info text-white mt-3"
                                    href="/testdrive"
                                >
                                    WATCH VIDEO
                                </Link>
                            </div>
                            <div className="col-lg-5 mt-4 mt-lg-0 text-center">
                                <div className="shield-container position-relative rounded p-4 bg-light">

                                    <i className="bi bi-shield shield-bg-icon"></i>

                                    <div className="shield-text d-flex flex-column justify-content-center align-items-center">
                                        <h5 className="text-dark mb-2">Military grade encryption</h5>

                                        <ul className="list-desc text-start mt-3">
                                            <li>Anonymous responses</li>
                                            <li>No personal data captured</li>
                                            <li className="mb-3">Zero subscription lock-in</li>
                                        </ul>

                                        <Link className="btn btn-primary mt-2" href="/pricing">
                                            SEE PRICING
                                        </Link>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                <section className="py-5 hand-banner text-white tryit-banner">
                    <div className="container instant-poll d-lg-flex align-items-center">
                        <div className="col-lg-6 text-white handfit">
                            <h2 className="mb-3">Want to Try It<br /> For Yourself?</h2>
                            <p className="lead">Instant insight in under 30 seconds. Pick an industry from below.</p>
                            <Link href="/register" className="btn btn-info text-white mt-3 position-relative zindexpointer">
                                REGISTER NOW
                            </Link>
                            <Link href={{ pathname: "/register", query: { type: "reseller" } }} className="btn btn-info text-white mt-3 position-relative zindexpointer">
                                BECOME A RESELLER
                            </Link>
                        </div>
                    </div>
                </section>

                <section className="py-5 bg-light">
                    <div className="container hear">
                        <h2 className="mb-5 text-purple">Hear From Our Users</h2>
                        <div className="row g-4">
                            <div className="col-md-4">
                                <div className="card quote">
                                    <p className='height110'>“Instant Polls changed how we run leadership meetings. We get clarity in minutes instead of waiting on surveys.”</p>
                                    <cite>Managing Director, Professional Services</cite>
                                </div>
                            </div>
                            <div className="col-md-4">
                                <div className="card quote">
                                    <p className='height110'>“As an event organiser, I finally have live engagement metrics to show sponsors real ROl.”</p>
                                    <cite>Event Manager</cite>
                                </div>
                            </div>
                            <div className="col-md-4">
                                <div className="card quote ">
                                    <p className='height110'>“Our students love it. Real-time feedback makes learning more interactive.”</p>
                                    <cite>University Lecturer</cite>
                                </div>
                            </div>

                        </div>
                    </div>
                </section>

                <section className="py-5 bg-light">
                    <div className="container">
                        <h2 className="mb-5 text-purple" ><Link href='/faq' className="text-purple text-underline"><u> Frequently Asked Questions </u></Link></h2>

                        {/* <div className="accordion" id="faqAccordion">
                            <div className="accordion-item">
                                <h2 className="accordion-header">
                                    <button
                                        className="accordion-button"
                                        type="button"
                                        data-bs-toggle="collapse"
                                        data-bs-target="#faq1"
                                    >
                                        What is real-time polling software?
                                    </button>
                                </h2>

                                <div
                                    id="faq1"
                                    className="accordion-collapse collapse show"
                                    data-bs-parent="#faqAccordion"
                                >
                                    <div className="accordion-body">
                                        Real-time polling software lets you create live polls and see responses instantly during meetings, events, or classes.
                                    </div>
                                </div>
                            </div>
                            <div className="accordion-item">
                                <h2 className="accordion-header">
                                    <button
                                        className="accordion-button collapsed"
                                        type="button"
                                        data-bs-toggle="collapse"
                                        data-bs-target="#faq2"
                                    >
                                        Can I use Instant Polls for meetings and events?
                                    </button>
                                </h2>

                                <div
                                    id="faq2"
                                    className="accordion-collapse collapse"
                                    data-bs-parent="#faqAccordion"
                                >
                                    <div className="accordion-body">
                                        Yes. Instant Polls helps teams, event hosts, and educators collect instant audience feedback in real time.
                                    </div>
                                </div>
                            </div>

                        </div> */}
                    </div>
                </section>


                <section className="py-5 final-cta">
                    <div className="container d-lg-flex cta-poll align-items-center">
                        <div className="col-lg-7">
                            <div className="final-copy">
                                <h2 className="">Ready to Ask Better Questions?</h2>
                                <p>Let’s build clarity into your <br />next decision moment.</p>
                                <div className="cta-btn">
                                    <Link href="/contact" className="button btn-info-new text-white me-3">BOOK A DEMO</Link>
                                    <Link href="/register" className="button btn-purple me-3">TRY A LIVE POLL</Link>
                                    <Link href="/pricing" className="button btn-other">SEE PRICING</Link>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            </div>
        </>
    )
}

Page.layout = 'public';
export default Page;